/**
 * A lightweight CodeMirror instance for editing Jadle snippets inline.
 * Shares syntax highlighting, autocomplete, and theming with the main editor
 * but has its own document, history, and lifecycle.
 * Does NOT update the main code atom or fire page navigation events.
 * @module InlineCodeMirror
 * @category Chat
 */

import {
	acceptCompletion,
	autocompletion,
	closeCompletion,
	moveCompletionSelection,
} from "@codemirror/autocomplete";
import {
	history,
	historyKeymap,
	indentWithTab,
	standardKeymap,
} from "@codemirror/commands";
import { syntaxHighlighting } from "@codemirror/language";
import { EditorState, Prec } from "@codemirror/state";
import { EditorView, highlightActiveLineGutter, keymap } from "@codemirror/view";
import {
	TextDecorations,
	getFilesByType,
} from "@quickpoint.me/shared";
import {
	QPTheme,
	QpHighlightStyle,
	currentLineGutter,
	pageLines,
	pageNumbersGutter,
	rYamlParser,
} from "@quickpoint.me/shared/editor";
import type { MutableRefObject } from "react";
import { useEffect, useRef } from "react";
import { useRecoilValue } from "recoil";
import {
	rYamlCompletions,
} from "../../lib/editor/autoComplete";
import { wrapSelection } from "../../lib/editor/helperPanel";
import {
	appVarsEffect,
	appVarsStateField,
	audioFilesEffect,
	audioFilesStateField,
	backLinksEffect,
	backLinksStateField,
	dataFilesEffect,
	dataFilesStateField,
	defaultFilterColors,
	filterColorsEffect,
	filterColorsStateField,
	filterNamesEffect,
	filterNamesStateField,
	imageFilesEffect,
	imageFilesStateField,
	loadedFilterFontsStateField,
} from "../../lib/editor/recoilStatesForCodeMirror";
import { CurrentFilterColorsAtom } from "../../state/atoms";
import { AllFiles } from "../../state/files";
import { FilterNamesSelector } from "../../state/filters";
import { StackCfKeyAtom } from "../../state/stackAtoms";
import { BackLinksSelector, StackId } from "../../state/stackSelectors";

interface InlineCodeMirrorProps {
	initialCode: string;
	editorViewRef?: MutableRefObject<EditorView | null>;
	onDocChange?: (code: string) => void;
}

function InlineCodeMirror({ initialCode, editorViewRef, onDocChange }: InlineCodeMirrorProps) {
	const onDocChangeRef = useRef(onDocChange);
	onDocChangeRef.current = onDocChange;
	const domRef = useRef<HTMLDivElement | null>(null);
	const internalViewRef = useRef<EditorView | null>(null);
	const viewRef = editorViewRef || internalViewRef;

	// Recoil state for populating autocomplete fields
	const allFiles = useRecoilValue(AllFiles);
	const backLinks = useRecoilValue(BackLinksSelector);
	const filterNames = useRecoilValue(FilterNamesSelector);
	const filterColors = useRecoilValue(CurrentFilterColorsAtom);
	const stackCfKey = useRecoilValue(StackCfKeyAtom);
	const stackId = useRecoilValue(StackId);

	const imageFiles = getFilesByType("image", allFiles);
	const dataFiles = getFilesByType("data", allFiles);
	const audioFiles = getFilesByType("audio", allFiles);

	// Create the editor once on mount
	// biome-ignore lint/correctness/useExhaustiveDependencies: Empty dependency array ensures this runs only on mount
	useEffect(() => {
		if (domRef.current && !viewRef.current) {
			const state = EditorState.create({
				doc: initialCode,
				extensions: [
					keymap.of([
						...standardKeymap,
						...historyKeymap,
						indentWithTab,
					]),
					Prec.highest(
						keymap.of([
							{ key: "Enter", run: acceptCompletion },
							{ key: "Escape", run: closeCompletion },
							{ key: "ArrowDown", run: moveCompletionSelection(true) },
							{ key: "ArrowUp", run: moveCompletionSelection(false) },
							{
								key: "Mod-b",
								run: (view) => {
									wrapSelection(TextDecorations.bold, view);
									return true;
								},
								preventDefault: true,
							},
							{
								key: "Mod-i",
								run: (view) => {
									wrapSelection(TextDecorations.italic, view);
									return true;
								},
								preventDefault: true,
							},
							{
								key: "Mod-u",
								run: (view) => {
									wrapSelection(TextDecorations.underline, view);
									return true;
								},
								preventDefault: true,
							},
						]),
					),
					rYamlParser(rYamlCompletions),
					autocompletion({ closeOnBlur: false }),
					// State fields for autocomplete data (each EditorState gets its own copy)
					audioFilesStateField,
					appVarsStateField,
					backLinksStateField,
					dataFilesStateField,
					filterColorsStateField.init(() => defaultFilterColors),
					filterNamesStateField,
					imageFilesStateField,
					loadedFilterFontsStateField,
					// Gutters (same as main CodeMirror)
					pageLines(),
					pageNumbersGutter,
					currentLineGutter,
					highlightActiveLineGutter(),
					// Visuals
					EditorView.lineWrapping,
					EditorView.contentAttributes.of({
						spellcheck: "true",
						autocapitalize: "on",
					}),
					history(),
					QPTheme,
					syntaxHighlighting(QpHighlightStyle, { fallback: true }),
					// Live preview: notify parent on every doc change
					EditorView.updateListener.of((update) => {
						if (update.docChanged && onDocChangeRef.current) {
							onDocChangeRef.current(update.state.doc.toString());
						}
					}),
				],
			});

			const view = new EditorView({
				state,
				parent: domRef.current,
			});
			viewRef.current = view;
		}

		return () => {
			if (viewRef.current) {
				viewRef.current.destroy();
				viewRef.current = null;
			}
		};
	}, []);

	// Populate autocomplete state fields directly via effects (bypasses DOM events)
	useEffect(() => {
		const view = viewRef.current;
		if (!view) return;
		view.dispatch({
			effects: [
				imageFilesEffect.of(imageFiles),
				audioFilesEffect.of(audioFiles),
				dataFilesEffect.of(dataFiles),
				filterColorsEffect.of(filterColors),
				filterNamesEffect.of(filterNames),
				appVarsEffect.of({ stackCfKey: stackCfKey || false, stackId }),
				backLinksEffect.of(backLinks),
			],
		});
	}, [imageFiles, audioFiles, dataFiles, filterColors, filterNames, stackCfKey, stackId, backLinks, viewRef]);

	return <div ref={domRef} className="inline-codemirror" />;
}

export default InlineCodeMirror;
